/**
 * Composes the full FullCalendar events array for manual scheduling mode.
 * Merges: booked slot + external busy times + past background.
 *
 * Ref: KInterviewScheduler/.../useCalendarEvents.tsx
 */
import { useMemo } from 'react';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import {
  EVENT_STYLES,
  EVENT_PRIORITY,
  BG_EVENT_CLASSES,
} from '../events/eventStyles';
import { splitEventAcrossDays } from '../events/eventHelpers';
import type { ScheduleSlot } from '../../state/useScheduleSharedState';

/** Busy blocks passed from `CalendarPanel` into FullCalendar via `useManualCalendarEvents`. */
export interface ManualCalendarExternalEvent {
  id?: string;
  start: string;
  end: string;
  title?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  /** Override the default `im-interviewer-busy` class — meeting rooms
   *  pass `im-meeting-room-busy` so CSS can render the muted palette. */
  classNames?: string;
  /** Richer title shown only on hover. Meeting-room events keep the
   *  friendly room name in `title` and stash the real meeting subject
   *  here so users can still see what blocked the room. */
  tooltipTitle?: string;
  /** Original status from the BE (`'busy' | 'oof' | 'unknown' | 'free'`).
   *  `'free'` flips the render to a dotted-border ghost block (BE sends
   *  user-marked-free events; legacy hid them entirely — we show them
   *  for visibility while keeping them out of slot math). Algorithms
   *  drop `'free'` upstream in `useBusyCalendarEvents.interviewerBusyForAlgorithm`. */
  status?: string;
}

interface UseManualCalendarEventsOptions {
  bookedSlot: ScheduleSlot | null;
  externalEvents?: ManualCalendarExternalEvent[];
  timezone: string;
  interviewName?: string;
}

export function useManualCalendarEvents({
  bookedSlot,
  externalEvents = [],
  timezone,
  interviewName,
}: UseManualCalendarEventsOptions) {
  const events = useMemo((): EventInput[] => {
    const evts: EventInput[] = [];

    // 1. External events (interviewer busy times / meeting-room blocks).
    // Foreground events so FullCalendar's column-split algorithm keeps
    // overlapping blocks side-by-side. Clicks are handled by the inline
    // onClick on BusyEventContent (mirrors legacy BusyEventContent.tsx).
    //
    // Multi-day events are split at midnight so each per-day segment
    // carries its own start/end time — without this, a 24h OOO from
    // Mon 9:30 PM → Tue 9:30 PM renders as "9:30 PM – 9:30 PM" on both
    // segments (FC's default per-segment label uses the global event
    // bounds). Matches the legacy preprocessing in
    // `schProcessCalendarEvents.ts#processFullDayEvents`.
    const splitExternalEvents = externalEvents.flatMap((evt) =>
      splitEventAcrossDays(evt, timezone)
    );
    for (let i = 0; i < splitExternalEvents.length; i += 1) {
      const evt = splitExternalEvents[i];
      const isFree = evt.status === 'free';
      evts.push({
        id: evt.id ? `${evt.id}-${i}` : `ext-${evt.start}-${evt.end}`,
        start: evt.start,
        end: evt.end,
        title: evt.title ?? 'Busy',
        // Free events render as background — they tint the cell behind
        // busy blocks without claiming a foreground column. Dashed
        // border + opacity still come from `.im-interviewer-free` CSS.
        display: isFree ? 'background' : undefined,
        // Free events keep the interviewer's pastel fill (so they stay
        // visually grouped with that user's busy blocks), but flip to a
        // dashed border via the `im-interviewer-free` class — distinct
        // enough to read as "marked free" without disappearing into the
        // grid.
        backgroundColor:
          evt.backgroundColor ?? EVENT_STYLES.busy.backgroundColor,
        // Busy: border matches fill (legacy parity).
        // Free: use the darker `textColor` instead so the dashed
        // pattern is actually visible against the pastel fill — without
        // contrast the dashes blend into the background and the block
        // looks identical to a busy one.
        borderColor: isFree
          ? (evt.textColor ?? EVENT_STYLES.busy.borderColor)
          : (evt.borderColor ??
            evt.backgroundColor ??
            EVENT_STYLES.busy.borderColor),
        textColor: evt.textColor ?? EVENT_STYLES.busy.textColor,
        classNames:
          evt.classNames ??
          (isFree ? 'im-interviewer-free' : 'im-interviewer-busy'),
        editable: false,
        extendedProps: {
          category: 'busy',
          priority: EVENT_PRIORITY.busy,
          tooltipTitle: evt.tooltipTitle,
          status: evt.status,
        },
      });
    }

    // 2. Booked slot — editable, draggable, resizable
    if (bookedSlot?.start && bookedSlot?.end) {
      evts.push({
        id: 'booked-slot',
        start: bookedSlot.start,
        end: bookedSlot.end,
        title: interviewName || 'Interview',
        backgroundColor: EVENT_STYLES.booked.backgroundColor,
        borderColor: EVENT_STYLES.booked.borderColor,
        textColor: EVENT_STYLES.booked.textColor,
        editable: true,
        durationEditable: true,
        startEditable: true,
        classNames: 'booked-event',
        extendedProps: {
          category: 'booked',
          priority: EVENT_PRIORITY.booked,
        },
      });
    }

    // 3. Past time background — grey overlay for all past times
    evts.push({
      start: moment()
        .tz(timezone)
        .subtract(10, 'year')
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      end: moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ'),
      display: 'background',
      classNames: BG_EVENT_CLASSES.disabledSlots,
      color: 'transparent',
    });

    return evts;
  }, [bookedSlot, externalEvents, timezone, interviewName]);

  return { events };
}
